import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatNumber } from '@/shared/lib/format';

import type { DashboardFailureReason } from '../types/dashboard.types';

/**
 * Why orders failed in the period: the five most frequent failure codes, each
 * linking to those failed orders. Orders that failed without a code are shown
 * as such — reconciliation lists them.
 */
export function FailureReasonsCard({
  reasons,
  from,
  to,
  isLoading,
}: {
  reasons: DashboardFailureReason[] | undefined;
  from: string;
  to: string;
  isLoading: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Top failure reasons</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && !reasons ? (
          <Skeleton className="h-28" />
        ) : !reasons?.length ? (
          <p className="text-muted-foreground text-sm">No failed orders in this period.</p>
        ) : (
          <ul className="divide-y" aria-label="Top failure reasons">
            {reasons.map((reason) => {
              const href =
                reason.code === null
                  ? '/reconciliation?check=failed_without_reason'
                  : `/orders?status=failed&failureCode=${encodeURIComponent(reason.code)}&from=${from}&to=${to}`;
              return (
                <li key={reason.code ?? 'none'}>
                  <Link
                    to={href}
                    className="hover:bg-muted/50 flex items-center justify-between gap-3 rounded-sm px-1 py-2 text-sm"
                  >
                    {reason.code === null ? (
                      <span className="text-muted-foreground">No failure code recorded</span>
                    ) : (
                      <code className="font-mono text-xs">{reason.code}</code>
                    )}
                    <span className="flex items-center gap-1">
                      <span className="font-semibold tabular-nums">
                        {formatNumber(reason.orders)}
                      </span>
                      <ChevronRight
                        className="text-muted-foreground size-4 rtl:rotate-180"
                        aria-hidden
                      />
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
